import type { Tag as TagType } from "@taskfortress/core"
import { useField } from "@taskfortress/core/src/form/hooks"
import { getTagColor } from "@taskfortress/core/src/utils/colors"
import { generateId } from "@taskfortress/core/src/utils/ids"
import React, { useState, useCallback, useEffect, useMemo } from "react"
import { ScrollView } from "react-native"
import { useAnalytics } from "../../providers/AnalyticsProvider"
import { useData } from "../../providers/DataProvider"
import { cn } from "../../styles/utils"
import type { PropsWithClassname } from "../../types"
import { Box } from "../Box"
import { Button } from "../Button"
import { Icon } from "../Icons"
import { Pressable } from "../Pressable"
import { Text } from "../Text"
import { TextInput } from "../form"
import { TagEditModal } from "./TagEditModal"

const ALL_TAGS_LABEL = "(all tags)"

interface Tag {
  id: string
  name: string
  color: string
}

type TagsPanelProps = PropsWithClassname & {
  visible?: boolean
  selectedTagId?: string | undefined
  onTagSelect?: (tagId: string | null) => void
}

export const TagsPanel: React.FC<TagsPanelProps> = ({
  className,
  visible = true,
  selectedTagId,
  onTagSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedTagForEdit, setSelectedTagForEdit] = useState<Tag | null>(null)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)

  const searchField = useField({
    name: "tagSearch",
    initialValue: "",
  })

  // Update searchQuery when field value changes
  useEffect(() => {
    setSearchQuery(searchField.value || "")
  }, [searchField.value])

  const analytics = useAnalytics()
  const { tags, updateGlobalTags } = useData()

  // Convert the DataProvider tags to DisplayTags with color
  const displayTags = useMemo((): Tag[] => {
    return tags.map((tag) => ({
      ...tag,
      color: tag.color || "#000000",
    }))
  }, [tags])

  const handleTagSelect = useCallback(
    (tagId: string, tagName: string) => {
      // If clicking on already selected tag, deselect it
      if (tagId === selectedTagId) {
        if (onTagSelect) onTagSelect(null)
      } else {
        if (onTagSelect) onTagSelect(tagId)
      }
      analytics.trackEvent("Tags", "Select", tagName)
    },
    [analytics, selectedTagId, onTagSelect],
  )

  const handleAllTagsSelect = useCallback(() => {
    if (onTagSelect) onTagSelect(null)
    analytics.trackEvent("Tags", "Select", ALL_TAGS_LABEL)
  }, [analytics, onTagSelect])

  const handleCreateTag = useCallback(
    async (tagName: string) => {
      if (tagName.trim()) {
        try {
          // Get the current tags from the state
          const currentTagNames = tags.map((tag) => tag.name)

          // Check if tag already exists
          if (!currentTagNames.includes(tagName.trim())) {
            // Add the new tag
            const updatedTags = [...tags]

            // Create new tag object with default color
            const newTag: TagType = {
              id: generateId(),
              name: tagName.trim(),
              color: getTagColor(tagName.trim()), // Set default color
            }

            updatedTags.push(newTag)

            // Update the global tags list
            await updateGlobalTags(updatedTags)

            // Clear search after creating a tag
            searchField.handleChange("")
            analytics.trackEvent("Tags", "Create", tagName.trim())
          }
        } catch (error) {
          console.error("Failed to add tag:", error)
        }
      }
    },
    [tags, updateGlobalTags, analytics, searchField],
  )

  // Handle edit icon click
  const handleEditClick = useCallback(
    (tag: Tag) => {
      setSelectedTagForEdit(tag)
      setIsEditModalVisible(true)
      analytics.trackEvent("Tags", "EditOpen", tag.name)
    },
    [analytics],
  )

  // Close edit modal
  const closeEditModal = useCallback(() => {
    setIsEditModalVisible(false)
    setSelectedTagForEdit(null)
  }, [])

  // Handle tag update
  const handleUpdateTag = useCallback(
    async (updatedTag: TagType) => {
      try {
        // Find and update the tag in tags array
        const updatedTags = tags.map((tag) =>
          tag.id === updatedTag.id ? updatedTag : tag,
        )

        // Update the global tags list
        await updateGlobalTags(updatedTags)

        analytics.trackEvent("Tags", "Update", updatedTag.name)
      } catch (error) {
        console.error("Failed to update tag:", error)
        throw error
      }
    },
    [tags, updateGlobalTags, analytics],
  )

  // Handle tag deletion
  const handleDeleteTag = useCallback(
    async (tagId: string) => {
      try {
        // Filter out the deleted tag
        const updatedTags = tags.filter((tag) => tag.id !== tagId)

        // Update the global tags list
        await updateGlobalTags(updatedTags)

        // If the deleted tag was selected, deselect it
        if (selectedTagId === tagId) {
          if (onTagSelect) onTagSelect(null)
        }

        analytics.trackEvent("Tags", "Delete", tagId)
      } catch (error) {
        console.error("Failed to delete tag:", error)
        throw error
      }
    },
    [tags, updateGlobalTags, analytics, selectedTagId, onTagSelect],
  )

  // Filter tags based on search query
  const filteredTags = useMemo(() => {
    return displayTags.filter((tag) =>
      tag.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [displayTags, searchQuery])

  // Sort tags alphabetically
  const sortedTags = useMemo(() => {
    return [...filteredTags].sort((a, b) => a.name.localeCompare(b.name))
  }, [filteredTags])

  // Check if search query doesn't match any existing tags
  const tagExists = useMemo(() => {
    return (
      !searchQuery ||
      tags.some((tag) => tag.name.toLowerCase() === searchQuery.toLowerCase())
    )
  }, [searchQuery, tags])

  // Check if "All Tags" should be visible
  const showAllTagsEntry = useMemo(() => {
    return (
      !searchQuery ||
      ALL_TAGS_LABEL.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [searchQuery])

  // Get all tag names for duplicate checking
  const allTagNames = useMemo(() => {
    return tags.map((tag) => tag.name)
  }, [tags])

  if (!visible) {
    return null
  }

  return (
    <Box className={cn("bg-background-0 h-full flex-col p-3", className)}>
      <Box className="border-b border-outline-200">
        <Box className="flex-row justify-between items-center mb-2">
          <Text className="font-medium text-xs text-typography-600">
            Tags{" "}
            <Text className="text-typography-400 ml-1">({tags.length})</Text>
          </Text>
        </Box>

        {/* Search input */}
        <Box className="relative">
          <TextInput
            value={searchField.value}
            handleChange={searchField.handleChange}
            name="tagSearch"
            placeholder="Add/search tags..."
            className="w-full"
            hideError
            rightIcon="search"
          />
        </Box>
      </Box>

      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        <Box className="py-2 gap-1">
          {showAllTagsEntry && (
            <Pressable
              className={cn(
                "flex-row items-center px-3 py-2 rounded-lg transition-colors border border-transparent",
                !selectedTagId
                  ? "border-primary-400 bg-primary-200 opacity-80"
                  : "active:bg-background-200",
              )}
              onPress={handleAllTagsSelect}
            >
              <Icon
                name="tag"
                className={cn(
                  "h-4 w-4 mr-2",
                  !selectedTagId ? "text-primary-600" : "text-typography-600",
                )}
              />
              <Text className={cn("text-sm italic text-typography-900")}>
                {ALL_TAGS_LABEL}
              </Text>
            </Pressable>
          )}

          {/* Create tag option when searching for a non-existent tag */}
          {searchQuery && !tagExists && (
            <Box className="px-3 py-2">
              <Button
                variant="primary"
                className="w-full flex-row items-center justify-center gap-1"
                onPress={() => handleCreateTag(searchQuery)}
              >
                <Icon
                  name="plus"
                  className="h-3 w-3 inline-block mr-2 text-typography-100"
                />
                <Text className="text-typography-100">
                  Create "{searchQuery}"
                </Text>
              </Button>
            </Box>
          )}

          {sortedTags.length > 0 &&
            sortedTags.map((tag) => (
              <Pressable
                key={tag.id}
                className={cn(
                  "flex-row items-center justify-between px-3 rounded-lg group border border-transparent",
                  selectedTagId === tag.id
                    ? "border-primary-400 bg-primary-200 opacity-80"
                    : "active:bg-background-200",
                )}
              >
                <Pressable
                  className="flex-row items-center flex-1 py-2"
                  onPress={() => handleTagSelect(tag.id, tag.name)}
                >
                  <Box
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: tag.color }}
                  />
                  <Text className={cn("text-sm")}>{tag.name}</Text>
                </Pressable>
                <Pressable
                  onPress={() => handleEditClick(tag)}
                  className={cn(
                    "p-1 rounded",
                    "hidden group-hover:flex group-active:flex hover:bg-background-100",
                  )}
                >
                  <Icon
                    name="edit"
                    className={cn(
                      "h-3.5 w-3.5",
                      selectedTagId === tag.id
                        ? "text-primary-700"
                        : "text-typography-400",
                    )}
                  />
                </Pressable>
              </Pressable>
            ))}
        </Box>
      </ScrollView>

      {/* Edit Modal */}
      {selectedTagForEdit && (
        <TagEditModal
          tag={selectedTagForEdit}
          isVisible={isEditModalVisible}
          onClose={closeEditModal}
          onSave={handleUpdateTag}
          onDelete={handleDeleteTag}
          existingTagNames={allTagNames}
        />
      )}
    </Box>
  )
}
